import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "next-themes";
import { Sun, Moon, Eye, EyeOff, Zap } from "lucide-react";

export default function LoginPage() {
  const { user, login, loading, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { theme, setTheme } = useTheme();

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <button
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        className="fixed top-4 right-4 p-2 rounded-sm hover:bg-muted text-muted-foreground"
        data-testid="login-theme-toggle"
      >
        {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
      </button>

      <div className="w-full max-w-sm">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-cyan-400 flex items-center justify-center flex-shrink-0">
              <Zap size={15} className="text-slate-900 fill-slate-900" />
            </div>
            <h1 className="font-heading text-3xl font-black tracking-tight text-foreground">
              signal<span className="text-cyan-400">Solution</span>
            </h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">Internes Betriebssystem</p>
        </div>

        <div className="bg-card border border-border p-6 rounded-sm">
          <h2 className="font-heading font-bold text-lg mb-5 text-foreground">Anmelden</h2>

          {error && (
            <div data-testid="login-error" className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="E-Mail Adresse"
                required
                data-testid="login-email-input"
                className="rounded-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="rounded-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full rounded-sm"
            >
              {submitting ? "Anmelden..." : "Anmelden"}
            </Button>
          </form>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Interner Zugang – nur für autorisierte Teammitglieder
        </p>
      </div>
    </div>
  );
}
